import React from 'react';
import { connect } from 'react-redux';
import loadable from '@loadable/component';
import PropTypes from 'prop-types';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import classNames from 'classnames';
import { useQuery, useQueryClient } from 'react-query';
import { getEventsById } from '@eb/event-renderer';

import * as analytics from '@eb/site-analytics';

import { trackLocationChange as trackLocationChangeAction } from '../../../../redux/actions/location';
import { trackEventClickInternal as trackEventClickInternalAction } from '../../../../redux/actions/analytics';
import { updateEventsWithExpansions as updateEventsWithExpansionsAction } from '../../../../redux/actions/expansions';
import { CollectionsBucket } from '../../../../components/FeaturedCollections';
import { WaypointLazyRender } from '../../../../components/WaypointLazyRender';
import { GA_CITY_BROWSE_CATEGORY } from '../../../../constants/analytics';
import {
    FEATURED_TEMPLATE,
    ENTITY_TYPE_MAP,
    LARGE_CARD_TEMPLATE_3,
    LARGE_CARD_TEMPLATE_6,
    CARD_TEMPLATE_4,
    CARD_TEMPLATE_8,
    CARD_BIG,
    COLLECTION_TEMPLATE,
    CATEGORY_TEMPLATE,
    NEARBY_CITIES_TEMPLATE,
    TRENDING_SEARCHES_TEMPLATE,
    ARTICLE_TEMPLATE,
    ORGANIZER_FOLLOW_TEMPLATE,
    EVENT_TYPE,
} from '../../../../constants';

const CardBucket = loadable(() => import('../../../../components/CardBucket'), {
    resolveComponent: (components) => components.CardBucket,
});

const OrganizerFollowBucket = loadable(
    () => import('../OrganizerFollowBucket'),
    {
        resolveComponent: (components) => components.OrganizerFollowBucket,
    },
);
const NearbyCitiesBucket = loadable(
    () => import('../../components/NearbyCitiesBucket'),
    {
        resolveComponent: (components) => components.NearbyCitiesBucket,
    },
);
const TrendingSearchesBucket = loadable(
    () => import('../../components/TrendingSearchesBucket'),
    {
        resolveComponent: (components) => components.TrendingSearchesBucket,
    },
);
const CategoryBucket = loadable(
    () => import('../../components/CategoryBucket'),
    {
        resolveComponent: (components) => components.CategoryBucket,
    },
);

const { trackEventFromState, trackEventFromStateOnPageUnload } = analytics;

export const BUCKET_LOOKUP_MAP = {
    [FEATURED_TEMPLATE]: CardBucket,
    [LARGE_CARD_TEMPLATE_3]: CardBucket,
    [LARGE_CARD_TEMPLATE_6]: CardBucket,
    [CARD_TEMPLATE_4]: CardBucket,
    [CARD_TEMPLATE_8]: CardBucket,
    [CARD_BIG]: CardBucket,
    [CATEGORY_TEMPLATE]: CategoryBucket,
    [NEARBY_CITIES_TEMPLATE]: NearbyCitiesBucket,
    [ARTICLE_TEMPLATE]: CardBucket,
    [TRENDING_SEARCHES_TEMPLATE]: TrendingSearchesBucket,
    [COLLECTION_TEMPLATE]: CollectionsBucket,
    [ORGANIZER_FOLLOW_TEMPLATE]: OrganizerFollowBucket,
};

const DecorativeImage = ({ decorationPlacement, decorativeImage }) => {
    let component = null;

    if (decorativeImage) {
        const classes = classNames(
            'decorative-image',
            `decorative-image--${decorationPlacement.position}`,
            decorationPlacement.classes,
        );
        const parentClasses = classNames(
            'decorative-image--container',
            decorationPlacement.parentClasses,
        );

        component = (
            <div className={parentClasses}>
                <img className={classes} src={decorativeImage} alt="" />
            </div>
        );
    }

    return component;
};

const shouldReturnEmpty = (content, type, template) => {
    /*Future proofing such that if api adds additional buckets app doesn't break*/
    /*
    If content is undefined, that means the bucket type map has the appropriate key,
    but the value associated to it necessary to access the actual content in the response
    has changed. Solution is we should send an error, but not let the app fall over.
    */
    if (!content || isEmpty(content)) {
        return true;
    }

    /* If the passed in type is not known in the ENTITY_TYPE_MAP this means that the API has
    started to return a layout format that is not known. The API should be allowed to progress
    without causing errors so no error should be fired. */
    if (!has(ENTITY_TYPE_MAP, type)) {
        return true;
    }

    /* If the passed in template is not known in the BUCKET_LOOKUP_MAP this means that the API has
    started to return a layout format that is not known. The API should be allowed to progress
    without causing errors so no error should be fired. */
    if (!has(BUCKET_LOOKUP_MAP, template)) {
        return true;
    }

    return false;
};

const EmptyBucket = (props) => {
    return <div key={props.type} className="empty-bucket" />;
};

/**
 * Hook which given a list of event IDs, runs
 * a call to search service to get the expanded versions
 *
 * @param {object} props
 */
const useBrowseEventExpansions = (props) => {
    const queryClient = useQueryClient();
    const queryKey = ['browse-buckets', props.placeId, props.isOnline];
    const queryData = queryClient.getQueryData(queryKey);
    const buckets =
        queryData?.pages?.reduce(
            (memo, page) => [...memo, ...page.buckets],
            [],
        ) || [];

    const currentBucket = buckets?.[props.bucketIndex] || {};
    const shouldExpand = currentBucket && !currentBucket.isContentExpanded;

    const bucketContentIds =
        currentBucket &&
        currentBucket.content &&
        currentBucket.type === EVENT_TYPE
            ? currentBucket.content.map(({ id }) => id)
            : [];

    return useQuery(
        ['expandEvents', bucketContentIds],
        async () => {
            const response = await getEventsById(bucketContentIds);

            queryClient.setQueryData(queryKey, (oldData) => {
                //create newBucket with formatted expanded events
                const newBucket = {
                    ...currentBucket,
                    isContentExpanded: true,
                    content: response.events,
                };

                //get page that this bucket belongs to
                const pageIndex = oldData.pages.findIndex((page) =>
                    page.buckets.some(
                        (bucket) => bucket.key === currentBucket.key,
                    ),
                );

                //get index of current bucket within page's context
                const bucketIndex = oldData.pages[pageIndex].buckets.findIndex(
                    (bucket) => bucket.key === currentBucket.key,
                );

                //create new buckets array with new data inserted
                const newBuckets = [
                    ...oldData.pages[pageIndex].buckets.slice(0, bucketIndex),
                    newBucket,
                    ...oldData.pages[pageIndex].buckets.slice(bucketIndex + 1),
                ];

                //create new pages array with page replaced with new buckets
                const newPages = [
                    ...oldData.pages.slice(0, pageIndex),
                    { ...oldData.pages[pageIndex], buckets: newBuckets },
                    ...oldData.pages.slice(pageIndex + 1),
                ];

                return {
                    pageParams: oldData.pageParams,
                    pages: newPages,
                };
            });
        },
        {
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            refetchOnMount: false,
            enabled:
                bucketContentIds.length > 0 &&
                shouldExpand &&
                currentBucket.type === EVENT_TYPE &&
                !props.isEmptyBucket &&
                props.inWindow,
        },
    );
};

export const BrowseBucket = (props) => {
    const { url: linkUrl } = props.seeMore || {};
    const isEmptyBucket = shouldReturnEmpty(
        props.content,
        props.type,
        props.template,
    );

    //Expand events in this bucket to get additional
    //data that we don't parse on the server
    useBrowseEventExpansions({
        ...props,
        isEmptyBucket,
        inWindow: props.inWindow,
    });

    if (isEmptyBucket && props.template !== 'organizer_follow') {
        return <EmptyBucket type={props.type} />;
    }

    const DynamicBucket = BUCKET_LOOKUP_MAP[props.template];

    return (
        <div className={props.containerClasses} key={props.bucketKey}>
            <div className={props.bucketWrapperClasses}>
                <DecorativeImage
                    decorationPlacement={props.decorationPlacement}
                    decorativeImage={props.decorativeImage}
                />
                <DynamicBucket
                    linkUrl={linkUrl}
                    onTrackEventClickInternal={(data) =>
                        props.trackEventClickInternal(data)
                    }
                    {...props}
                />
            </div>
        </div>
    );
};

const LazyRenderBrowseBucket = (props) => {
    const { bucketIndex, onEnter } = props;
    const [inWindow, setIsInWindow] = React.useState(false);

    const handleEnter = () => {
        setIsInWindow(true);
        onEnter?.();
    };

    return (
        <WaypointLazyRender
            initialIsVisible={props.isBotRequest || bucketIndex <= 3}
            emptyStateStyles={{ height: '300px' }}
            onEnter={handleEnter}
        >
            <BrowseBucket {...props} inWindow={inWindow} />
        </WaypointLazyRender>
    );
};

BrowseBucket.propTypes = {
    allEventsUrl: PropTypes.string,
    bucketKey: PropTypes.string,
    bucketWrapperClasses: PropTypes.string,
    currentPlace: PropTypes.string,
    containerClasses: PropTypes.string,
    //eslint-disable-next-line
    content: PropTypes.arrayOf(PropTypes.object),
    //eslint-disable-next-line
    decorationPlacement: PropTypes.object,
    decorativeImage: PropTypes.string,
    httpReferer: PropTypes.string,
    isAuthenticated: PropTypes.bool,
    linkUrl: PropTypes.string,
    locationSlug: PropTypes.string,
    name: PropTypes.string,
    onTrackEventClickInternal: PropTypes.func,
    template: PropTypes.string,
    trackLocationChange: PropTypes.func,
    trackGAEvent: PropTypes.func,
    trackGAEventOnPageUnload: PropTypes.func,
    type: PropTypes.string,
    seeMoreMarkedNoFollow: PropTypes.bool,
    isGDPRCountry: PropTypes.bool,
    featureFlags: PropTypes.object,
    isBotRequest: PropTypes.bool,
};

export const _mapStateToProps = (state, ownProps) => {
    const {
        containerClasses,
        bucketWrapperClasses,
        decorationPlacement,
        decorativeImage,
    } = ownProps;

    return {
        tld: state.app.tld,
        containerClasses,
        bucketWrapperClasses,
        decorationPlacement,
        decorativeImage,
        currentPlace: state.location.currentPlace,
        currentPlaceParent: state.location.currentPlaceParent,
        placeType: state.location.placeType,
        isAuthenticated: state.auth.isAuthenticated,
        allEventsUrl: state.content.allEventsUrl,
        httpReferer: state.app.httpReferer,
        locationSlug: state.location.slug,
        domain: state.app.domain,
        featureFlags: state.app.featureFlags,
        experiments: state.app.experiments,
        userEmail: state.app.userEmail,
        publicId: state.app.publicId,
        userId: state.app.publicId,
        placeId: state.location.placeId,
        isOnline: state.location.isOnline,
        isBotRequest: state.app.isBotRequest,
        isGDPRCountry: state.app.isGDPRCountry,
        gaCategory: GA_CITY_BROWSE_CATEGORY,
        gaActionSuffix: 'CityBrowse',
        gaSettings: state.app.gaSettings,
        trackGAEvent: (action) =>
            trackEventFromState(state, {
                category: GA_CITY_BROWSE_CATEGORY,
                ...action,
            }),
        trackGAEventOnPageUnload: (action) =>
            trackEventFromStateOnPageUnload(state, {
                category: GA_CITY_BROWSE_CATEGORY,
                ...action,
            }),
    };
};
const _mapDispatchToProps = (dispatch) => ({
    trackLocationChange: (...args) =>
        dispatch(trackLocationChangeAction(...args)),
    trackEventClickInternal: (...args) =>
        dispatch(trackEventClickInternalAction(...args)),
    updateEventsWithExpansions: (...args) =>
        dispatch(updateEventsWithExpansionsAction(...args)),
});

export const BrowseBucketContainer = connect(
    _mapStateToProps,
    _mapDispatchToProps,
)(LazyRenderBrowseBucket);

export default BrowseBucketContainer;
